import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function PdfFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingPost, setEditingPost] = useState(null);

  const categories = [
    { id: "all", name: "Todos" },
    { id: "books", name: "Livros" },
    { id: "academic", name: "Acadêmico" },
    { id: "business", name: "Negócios" },
    { id: "legal", name: "Jurídico" },
    { id: "manuals", name: "Manuais" },
    { id: "reports", name: "Relatórios" },
    { id: "other", name: "Outros" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/pdf?page=${pageNum}&limit=20`;
      if (category !== "all") url += `&category=${category}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching PDFs:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [category]);

  const handleEditPost = (post) => {
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  if (loading && posts.length === 0) {
    return <div className="pdf_feed_loading"><div className="pdf_loader"></div><span>Carregando PDFs...</span></div>;
  }

  return (
    <div className="pdf_feed">
      <div className="pdf_header">
        <h1>Documentos PDF</h1>
        <p>Biblioteca de documentos da comunidade</p>
      </div>

      <div className="pdf_filters">
        {categories.map((cat) => (
          <button key={cat.id} className={category === cat.id ? "active" : ""} onClick={() => setCategory(cat.id)}>
            {cat.name}
          </button>
        ))}
      </div>

      {posts.length === 0 ? (
        <div className="pdf_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/>
          </svg>
          <h3>Nenhum PDF encontrado</h3>
          <p>Seja o primeiro a compartilhar um documento!</p>
        </div>
      ) : (
        <div className="pdf_list">
          {posts.map((post) => (
            <div key={post._id} className="pdf_item">
              <div className="pdf_icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                  <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/>
                </svg>
              </div>
              <div className="pdf_info">
                <h3>{post.pdfData?.title || post.text || "Documento PDF"}</h3>
                {post.pdfData?.description && (
                  <p className="pdf_description">{post.pdfData.description}</p>
                )}
                <div className="pdf_meta">
                  <span>{formatFileSize(post.pdfData?.fileSize)}</span>
                  {post.pdfData?.pages && <span>{post.pdfData.pages} páginas</span>}
                  <span>•</span>
                  <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                </div>
                <Link to={`/profile/${post.user.username}`} className="pdf_author">
                  <img src={post.user.picture} alt="" />
                  <span>{post.user.first_name} {post.user.last_name}</span>
                </Link>
              </div>
              <div className="pdf_actions">
                <a
                  href={post.pdfData?.fileUrl || post.images?.[0]?.url || post.images?.[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pdf_download_btn"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                  Baixar
                </a>
                <div onClick={(e) => e.stopPropagation()}>
                  <FeedPostMenu
                    post={post}
                    onEdit={handleEditPost}
                    onPostDeleted={handlePostDeleted}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="pdf_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais documentos"}
          </button>
        </div>
      )}
      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
